import { useContext } from 'react';
import {
  getFeatureFlagByNameSelector,
  headerTaxoTrendingSearchesSelector,
  progressiveHeaderTaxoTrendingSearchesSelector,
} from 'store/selectors';
import { useAppSelector } from 'store/store';
import { WindowContext } from 'utility/windowContext';
import { PERSONALIZATION_DATA_KEYS } from 'utility/constants';
import useTrendingSearchDataFromWindow from './useTrendingSearchDataFromWindow';
import usePersonalization from './usePersonalization';

const useTrendingSearches = () => {
  // get trending searches data from taxo. It can come from either progressive taxo or the original
  // header taxo depending on whether the desktopProgressiveTaxo feature flag is set
  const progressiveHeaderDesktopEnabled = useAppSelector(
    getFeatureFlagByNameSelector('progressiveTaxoDesktop'),
  );

  const headerTrendingSearches = useAppSelector(headerTaxoTrendingSearchesSelector);
  const progressiveTrendingSearches = useAppSelector(progressiveHeaderTaxoTrendingSearchesSelector);
  const trendingSearchHomrData = progressiveHeaderDesktopEnabled
    ? progressiveTrendingSearches
    : headerTrendingSearches;

  // get trending searches data from the window if it's set
  const { trendingSearchWindowData } = useTrendingSearchDataFromWindow();

  // determine if we're on the homepage and if trending searches is enabled there
  const win = useContext(WindowContext);
  const isHomepage = win?.location.pathname === '/';
  const disableTrendingSearchBarOnHomePage = useAppSelector(
    getFeatureFlagByNameSelector('disableTrendingSearchBarOnHomePage'),
  );

  // determine if trending searches is enabled via feature flag
  const trendingSearchBarEnabled = useAppSelector(
    getFeatureFlagByNameSelector('trendingSearchBarEnabled'),
  );

  const showTrendingSearchData =
    (isHomepage && !disableTrendingSearchBarOnHomePage) || trendingSearchBarEnabled;

  const { personalizationData: trendingSearchesPersonalizationData, isLoadingPersonalization } =
    usePersonalization({
      flagName: 'personalization_trendingSearches',
      personalizationKey: PERSONALIZATION_DATA_KEYS.MODIFY_TRENDING_SEARCHES,
      originalData: (trendingSearchWindowData ?? trendingSearchHomrData) || null,
    });

  if (isLoadingPersonalization && showTrendingSearchData) {
    return {
      trendingSearchData: null,
      showTrendingSearchData: true,
      isLoading: true,
    };
  }

  const trendingSearchData =
    trendingSearchesPersonalizationData ?? trendingSearchWindowData ?? trendingSearchHomrData;

  // If we're on the homepage and trending searches hasn't been disabled, return the trending search
  // data with "showTrendingSearchData" set to "true"
  if (isHomepage && !disableTrendingSearchBarOnHomePage) {
    return { trendingSearchData, showTrendingSearchData: true, isLoading: false };
  }

  // otherwise, if trending searches is enabled by feature flag, return it that way with
  // "showTrendingSearchData" set to true
  if (trendingSearchBarEnabled) {
    return { trendingSearchData, showTrendingSearchData: true, isLoading: false };
  }

  // finally, if none of those conditions is true, return the data, but set "showTrendingSearchData"
  // to false
  return { trendingSearchData, showTrendingSearchData: false, isLoading: false };
};

export default useTrendingSearches;
